/**
 * Taxonomía muscular: alimenta el mapa de calor y el balance de §5.2, y la detección de
 * solapamiento de §6.3.
 */

import type { Ejercicio, GrupoMuscular, Musculo, Region } from './types.ts';

interface InfoMusculo {
  nombre: string;
  grupo: GrupoMuscular;
  region: Region;
}

export const MUSCULOS: Record<Musculo, InfoMusculo> = {
  pecho: { nombre: 'Pecho', grupo: 'pecho', region: 'superior' },
  dorsales: { nombre: 'Dorsales', grupo: 'espalda', region: 'superior' },
  espalda_alta: { nombre: 'Espalda alta', grupo: 'espalda', region: 'superior' },
  lumbar: { nombre: 'Lumbar', grupo: 'espalda', region: 'superior' },
  trapecio: { nombre: 'Trapecio', grupo: 'espalda', region: 'superior' },
  hombro_anterior: { nombre: 'Hombro anterior', grupo: 'hombros', region: 'superior' },
  hombro_lateral: { nombre: 'Hombro lateral', grupo: 'hombros', region: 'superior' },
  hombro_posterior: { nombre: 'Hombro posterior', grupo: 'hombros', region: 'superior' },
  biceps: { nombre: 'Bíceps', grupo: 'brazos', region: 'superior' },
  triceps: { nombre: 'Tríceps', grupo: 'brazos', region: 'superior' },
  antebrazo: { nombre: 'Antebrazo', grupo: 'brazos', region: 'superior' },
  core: { nombre: 'Core', grupo: 'core', region: 'core' },
  oblicuos: { nombre: 'Oblicuos', grupo: 'core', region: 'core' },
  cuadriceps: { nombre: 'Cuádriceps', grupo: 'pierna', region: 'inferior' },
  isquios: { nombre: 'Isquiotibiales', grupo: 'pierna', region: 'inferior' },
  gluteos: { nombre: 'Glúteos', grupo: 'pierna', region: 'inferior' },
  aductores: { nombre: 'Aductores', grupo: 'pierna', region: 'inferior' },
  gemelos: { nombre: 'Gemelos', grupo: 'pierna', region: 'inferior' },
};

export const TODOS_LOS_MUSCULOS = Object.keys(MUSCULOS) as Musculo[];

export const GRUPOS: GrupoMuscular[] = ['pecho', 'espalda', 'hombros', 'brazos', 'core', 'pierna'];

export const NOMBRE_GRUPO: Record<GrupoMuscular, string> = {
  pecho: 'Pecho',
  espalda: 'Espalda',
  hombros: 'Hombros',
  brazos: 'Brazos',
  core: 'Core',
  pierna: 'Pierna',
};

export function nombreMusculo(m: Musculo): string {
  return MUSCULOS[m]?.nombre ?? m;
}

export function grupoDe(m: Musculo): GrupoMuscular | null {
  return MUSCULOS[m]?.grupo ?? null;
}

export function regionDe(m: Musculo): Region | null {
  return MUSCULOS[m]?.region ?? null;
}

export function musculosDelGrupo(g: GrupoMuscular): Musculo[] {
  return TODOS_LOS_MUSCULOS.filter((m) => MUSCULOS[m].grupo === g);
}

/**
 * Cuánto carga un ejercicio a cada músculo. El primario cuenta entero; los secundarios
 * cuentan a mitad, que es la convención habitual para "series efectivas": un press de
 * banca estimula tríceps de verdad, pero no como un press de tríceps.
 */
export const PESO_SECUNDARIO = 0.5;

export function cargaMuscular(ej: Ejercicio): Map<Musculo, number> {
  const carga = new Map<Musculo, number>();
  if (ej.tipo === 'cardio') return carga;
  carga.set(ej.musculo_primario, 1);
  for (const m of ej.musculos_secundarios) {
    if (m === ej.musculo_primario) continue;
    carga.set(m, Math.max(carga.get(m) ?? 0, PESO_SECUNDARIO));
  }
  return carga;
}

/**
 * Solapamiento entre dos ejercicios: suma del producto de cargas por músculo compartido.
 * Dos press que comparten tríceps como secundario dan 0.25; un press de tríceps seguido de
 * un press de hombro (tríceps primario en uno, secundario en el otro) da 0.5.
 */
export function solapamiento(a: Ejercicio, b: Ejercicio): { score: number; musculos: Musculo[] } {
  const ca = cargaMuscular(a);
  const cb = cargaMuscular(b);
  let score = 0;
  const musculos: Musculo[] = [];
  for (const [m, va] of ca) {
    const vb = cb.get(m);
    if (vb === undefined) continue;
    score += va * vb;
    musculos.push(m);
  }
  return { score: Number(score.toFixed(4)), musculos };
}

/**
 * Solapamiento entre dos conjuntos de ejercicios (dos días de rutina). Se toma la carga
 * máxima por músculo dentro de cada día, no la suma: que un día tenga tres ejercicios que
 * tocan tríceps no lo hace tres veces más conflictivo con el día siguiente.
 */
export function solapamientoEntreDias(
  diaA: Ejercicio[],
  diaB: Ejercicio[],
): { score: number; musculos: Musculo[] } {
  const acumular = (ejs: Ejercicio[]): Map<Musculo, number> => {
    const total = new Map<Musculo, number>();
    for (const ej of ejs) {
      for (const [m, v] of cargaMuscular(ej)) {
        total.set(m, Math.max(total.get(m) ?? 0, v));
      }
    }
    return total;
  };

  const ca = acumular(diaA);
  const cb = acumular(diaB);
  let score = 0;
  const musculos: Musculo[] = [];
  for (const [m, va] of ca) {
    const vb = cb.get(m);
    if (vb === undefined) continue;
    score += va * vb;
    musculos.push(m);
  }
  musculos.sort((x, y) => (cb.get(y)! * ca.get(y)!) - (cb.get(x)! * ca.get(x)!));
  return { score: Number(score.toFixed(4)), musculos };
}

/**
 * Umbral por encima del cual dos días consecutivos se consideran en conflicto de
 * recuperación (§6.3). 0.5 = un músculo compartido con carga alta en ambos días, o dos
 * compartidos como secundarios en ambos.
 */
export const UMBRAL_SOLAPAMIENTO = 0.5;
